import { useEffect, useState, type FormEvent } from 'react'
import { api, type BenutzerEintrag, type Schluesselstand } from '../api'

/**
 * Nur fuer Admins, aufgebaut wie in Dolly: Einladung per Mail, Einladung erneut senden, Reset-Link senden,
 * dazu Sperren und Loeschen (samt Daten und Aufnahmen).
 */
export function Verwaltung({ ich }: { ich: string }) {
  const [benutzer, setBenutzer] = useState<BenutzerEintrag[]>([])
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null)

  const laden = () => api.benutzer().then(setBenutzer).catch((e) => setMeldung({ text: (e as Error).message, fehler: true }))
  useEffect(() => {
    laden()
  }, [])

  const aktion = async (f: () => Promise<unknown>, erfolg: string) => {
    setMeldung(null)
    try {
      await f()
      setMeldung({ text: erfolg, fehler: false })
      await laden()
    } catch (e) {
      setMeldung({ text: (e as Error).message, fehler: true })
    }
  }

  return (
    <div className="verwaltung">
      <a href="#konto" className="zurueck">
        ‹ Konto
      </a>

      {meldung && <p className={meldung.fehler ? 'hinweis fehler' : 'hinweis'}>{meldung.text}</p>}

      <Einladung
        onAngelegt={(b) => {
          setMeldung({ text: `${b.benutzername} angelegt, Einladung an ${b.email} verschickt.`, fehler: false })
          laden()
        }}
        onFehler={(text) => setMeldung({ text, fehler: true })}
      />

      <Verschluesselung />

      <h3>Benutzer</h3>
      <ul className="liste">
        {benutzer.map((b) => (
          <li key={b.id} className="karte benutzer-zeile">
            <div>
              <strong>{b.benutzername}</strong>
              {b.rolle === 'ADMIN' && <span className="chip">Admin</span>}
              {!b.aktiv && <span className="chip gesperrt">gesperrt</span>}
            </div>
            <p className="leise klein">
              {b.email ?? 'keine E-Mail-Adresse'}
              {b.email && !b.emailVerifiziert && ' (nicht bestätigt)'}
            </p>
            <p className="leise klein">
              {!b.hatPasswort
                ? b.einladungBis && new Date(b.einladungBis) > new Date()
                  ? `Einladung offen bis ${new Date(b.einladungBis).toLocaleDateString('de-DE')}`
                  : 'Einladung abgelaufen'
                : `${b.anzahlNotizen} Notiz(en) · seit ${new Date(b.erstelltAm).toLocaleDateString('de-DE')}`}
            </p>
            {b.benutzername !== ich && (
              <div className="knoepfe links">
                {!b.hatPasswort && b.email && (
                  <button className="link" onClick={() => aktion(() => api.einladungSenden(b.id), `Einladung an ${b.email} verschickt.`)}>
                    Einladung erneut senden
                  </button>
                )}
                {b.hatPasswort && b.emailVerifiziert && (
                  <button className="link" onClick={() => aktion(() => api.resetSenden(b.id), `Link zum Zurücksetzen an ${b.email} verschickt.`)}>
                    Reset-Link senden
                  </button>
                )}
                {b.aktiv ? (
                  <button className="link" onClick={() => aktion(() => api.benutzerSperren(b.id), `${b.benutzername} gesperrt.`)}>
                    Sperren
                  </button>
                ) : (
                  <button className="link" onClick={() => aktion(() => api.benutzerEntsperren(b.id), `${b.benutzername} entsperrt.`)}>
                    Entsperren
                  </button>
                )}
                <button
                  className="link gefahr"
                  onClick={() =>
                    prompt(
                      `${b.benutzername} mit ${b.anzahlNotizen} Notiz(en), allen Personen, Terminen und Aufnahmen endgültig löschen?\nZum Bestätigen den Benutzernamen eingeben:`,
                    ) === b.benutzername && aktion(() => api.benutzerLoeschen(b.id), `${b.benutzername} gelöscht.`)
                  }
                >
                  Löschen
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Einladung({ onAngelegt, onFehler }: { onAngelegt: (b: BenutzerEintrag) => void; onFehler: (text: string) => void }) {
  const [benutzername, setBenutzername] = useState('')
  const [email, setEmail] = useState('')
  const [admin, setAdmin] = useState(false)
  const [laeuft, setLaeuft] = useState(false)

  const absenden = async (e: FormEvent) => {
    e.preventDefault()
    setLaeuft(true)
    try {
      const b = await api.benutzerEinladen(benutzername.trim(), email.trim(), admin ? 'ADMIN' : 'NUTZER')
      setBenutzername('')
      setEmail('')
      setAdmin(false)
      onAngelegt(b)
    } catch (err) {
      onFehler((err as Error).message)
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <form className="karte formular" onSubmit={absenden}>
      <h3>Jemanden einladen</h3>
      <label>
        Benutzername
        <input value={benutzername} onChange={(e) => setBenutzername(e.target.value)} pattern="[A-Za-z0-9._\-]{3,50}" title="3–50 Zeichen: Buchstaben, Ziffern, . _ -" required />
      </label>
      <label>
        E-Mail-Adresse
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <label className="auswahl-zeile">
        <input type="checkbox" checked={admin} onChange={(e) => setAdmin(e.target.checked)} /> darf selbst Benutzer verwalten
      </label>
      <p className="leise klein">Die Person bekommt eine Mail mit einem Link, über den sie ihr Passwort vergibt. Der Link ist 7 Tage gültig.</p>
      <div className="knoepfe">
        <button type="submit" disabled={laeuft || !benutzername || !email}>
          Anlegen und einladen
        </button>
      </div>
    </form>
  )
}

/**
 * Stand der Verschluesselung nach einem Schluesselwechsel: Solange mit einem alten Schluessel noch etwas
 * verschluesselt ist, muss er in ENCRYPTION_KEYS bleiben. Umgestellt wird im Hintergrund (alle 10 Minuten).
 */
function Verschluesselung() {
  const [stand, setStand] = useState<Schluesselstand | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    api.schluesselstand().then(setStand).catch((e) => setFehler((e as Error).message))
  }, [])

  const jetzt = async () => {
    setLaeuft(true)
    setFehler(null)
    try {
      setStand(await api.umschluesseln())
    } catch (e) {
      setFehler((e as Error).message)
    } finally {
      setLaeuft(false)
    }
  }

  if (!stand) return fehler ? <p className="hinweis fehler">{fehler}</p> : null
  const alte = stand.konfiguriert.filter((id) => id !== stand.aktiv)
  const offen = Object.entries(stand.verbleibend)

  return (
    <section className="karte">
      <h3>Verschlüsselung</h3>
      <p className="leise klein">
        Aktiver Schlüssel: {stand.aktiv}
        {alte.length > 0 && ` · außerdem konfiguriert: ${alte.join(', ')}`}
      </p>
      {offen.length > 0 ? (
        <>
          <p className="klein">
            Noch umzustellen: {offen.map(([id, n]) => `${n} Wert(e) mit Schlüssel ${id}`).join(', ')}. Diese Schlüssel müssen in ENCRYPTION_KEYS
            bleiben, bis hier nichts mehr steht.
          </p>
          <button className="zweitrangig" onClick={jetzt} disabled={laeuft}>
            {laeuft ? 'Stelle um …' : 'Jetzt umstellen'}
          </button>
        </>
      ) : (
        alte.length > 0 && (
          <p className="klein">Alles ist mit Schlüssel {stand.aktiv} verschlüsselt. Die übrigen Schlüssel können aus ENCRYPTION_KEYS entfernt werden.</p>
        )
      )}
      {fehler && <p className="fehler">{fehler}</p>}
    </section>
  )
}
